import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    Typography,
    useTheme,
} from '@mui/material';
import { Check, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { getModalityIcon, Modality } from '~/domain/dicom/modality';

interface ExportListItemProps {
    expanded: boolean;
    onChange: (event: React.SyntheticEvent, isExpanded: boolean) => void;
    isSelected: boolean;
    onSelect: () => void;
    label: string;
    modality: Modality;
    isExported: boolean;
    details: React.ReactNode;
}

const ExportListItem: React.FC<ExportListItemProps> = ({
    expanded,
    onChange,
    isSelected,
    onSelect,
    label,
    modality,
    isExported,
    details,
}) => {
    const { palette } = useTheme();
    return (
        <Box>
            <Accordion
                expanded={expanded}
                onChange={onChange}
                sx={(theme) => ({
                    overflowY: 'auto',
                    border: `1px solid ${theme.palette.divider}`,
                    borderBottom: 'none',
                    p: 0,
                })}
                square
            >
                <AccordionSummary
                    expandIcon={<ChevronRight size={16} />}
                    sx={(theme) => ({
                        ml: 5,
                        minHeight: 0,
                        '& .MuiAccordionSummary-content': { my: 1 },
                        backgroundColor: isExported
                            ? `${theme.palette.success.light}20` // 20 for opacity
                            : 'default',
                    })}
                    aria-controls="panel-content"
                    id="panel-header"
                >
                    <Box
                        width="100%"
                        display="flex"
                        gap={2}
                        alignItems="center"
                    >
                        <Checkbox
                            data-testid={`exportItemCheckbox${label}`}
                            sx={{
                                position: 'absolute',
                                left: -35,
                            }}
                            onClick={(e) => e.stopPropagation()}
                            checked={isSelected}
                            onChange={onSelect}
                        />
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            width="100%"
                        >
                            <Typography component="span" fontSize={14}>
                                {label}
                            </Typography>
                            {isExported ? (
                                <Typography
                                    fontSize={12}
                                    component="span"
                                    color="success.main"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <Check
                                        size="14px"
                                        color={palette.success.main}
                                    />
                                    Exported
                                </Typography>
                            ) : (
                                <Typography
                                    fontSize={12}
                                    component="span"
                                    color="text.secondary"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    {getModalityIcon(modality, 12)}
                                    {modality}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1, pl: 6 }}>
                    {details}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default ExportListItem;
